import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import translations from "./translations";

const I18nContext = createContext(null);

const SUPPORTED = ["it", "en", "de", "es", "fr", "ja"];
const DEFAULT_LANG = "it";
const STORAGE_KEY = "mabw_lang";

function detectInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const stored = window.localStorage?.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;
  const nav = (window.navigator?.language || "").toLowerCase().slice(0, 2);
  if (SUPPORTED.includes(nav)) return nav;
  return DEFAULT_LANG;
}

function resolvePath(obj, path) {
  return path.split(".").reduce((acc, k) => (acc != null ? acc[k] : undefined), obj);
}

function interpolate(str, params) {
  if (!params || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (params[k] != null ? String(params[k]) : `{${k}}`));
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try { window.localStorage?.setItem(STORAGE_KEY, next); } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[i18n] localStorage unavailable:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const t = useCallback(
    (key, params) => {
      const direct = resolvePath(translations[lang], key);
      if (direct !== undefined) return interpolate(direct, params);
      const fallback = resolvePath(translations[DEFAULT_LANG], key);
      if (fallback !== undefined) return interpolate(fallback, params);
      return key;
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, supported: SUPPORTED }),
    [lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export const useT = () => useI18n().t;
